import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { useNavigate } from 'react-router-dom';
import { 
    FaFilter, FaArrowRight, FaTrash, FaPlusCircle, 
    FaExchangeAlt, FaLayerGroup, FaLock, FaExclamationTriangle
} from 'react-icons/fa';

import { SuccessModal, ErrorModal, ConfirmModal, InfoModal, BaseModal } from './SharedComponents/Modals';

const ModifyTest = ({ auth, onSuccess }) => {
    const navigate = useNavigate();

    // --- 1. DEFINIZIONE DEGLI HOOKS (DEVE ESSERE SEMPRE ALL'INIZIO) ---

    // Configurazione Tema
    const theme = {
        primary: '#0C4A76',
        secondary: '#11A0D9',
        accent: '#F2962B',
        bg: '#f8f9fa',
        danger: '#dc3545'
    };

    // Stati Dati
    const [tenants, setTenants] = useState([]);
    const [classes, setClasses] = useState([]);
    const [mode, setMode] = useState('append'); 
    
    // Filtri
    const [filters, setFilters] = useState({
        tenant_id: auth.role === 'superadmin' ? '' : auth.tenant_id,
        class_id: ''
    });

    const [availableTests, setAvailableTests] = useState([]);
    const [destinationTest, setDestinationTest] = useState(null);
    const [selectedSources, setSelectedSources] = useState([]);
    
    // Stato Picker Domande
    const [questionPicker, setQuestionPicker] = useState({
        show: false, sourceTest: null, questions: [], selectedIds: [], filterText: ''
    });

    const [loading, setLoading] = useState(false);
    const [modalState, setModalState] = useState({ type: null, message: '', title: '', confirmAction: null });

    // Helper chiusura modali
    const closeModal = () => {
        const type = modalState.type;
        setModalState({ type: null });
        if (type === 'success') {
            if (onSuccess) onSuccess();
            navigate('/tests');
        }
    };

    // --- 2. EFFECT PER REDIRECT E CARICAMENTI ---

    // Redirect se utente non autorizzato
    useEffect(() => {
        if (!auth || auth.role === 'user') {
            navigate('/dashboard');
        }
    }, [auth, navigate]);

    // Caricamento Tenants (Solo Superadmin)
    useEffect(() => {
        if (auth.role === 'superadmin') {
            axios.get('/api_tenants.php').then(res => { if (res.data.success) setTenants(res.data.data); });
        }
    }, [auth.role]);

    // Recupero Test
    useEffect(() => {
        if (auth.role === 'superadmin' && !filters.tenant_id) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    action: 'get_tests', 
                    tenant_id: filters.tenant_id,
                    class_id: filters.class_id,
                    user_id: auth.user_id, 
                    role: auth.role        
                });

                const res = await axios.get(`/api_modify_test.php?${params.toString()}`);
                if (res.data.success) {
                    setClasses(res.data.classes);
                    setAvailableTests(res.data.tests);
                }
            } catch (err) {
                setModalState({ type: 'error', title: 'Errore', message: 'Impossibile caricare i test.' });
            } finally {
                setLoading(false);
            }
        };

        // Esegui solo se l'utente è valido
        if (auth && auth.role !== 'user') {
            fetchData();
        }
    }, [filters.tenant_id, filters.class_id, auth, auth.role, auth.user_id]);

    // --- 3. EARLY RETURN (SOLO ORA È SICURO FARLO) ---
    // Se l'utente non è autorizzato, non mostrare nulla (il redirect è gestito dall'useEffect sopra)
    if (!auth || auth.role === 'user') return null;

    // --- 4. LOGICA FUNZIONI ---

    const getFilteredSourceTests = () => {
        let list = availableTests.filter(t => 
            (!destinationTest || t.id !== destinationTest.id) &&
            !selectedSources.find(s => s.id === t.id)
        );

        if (mode === 'merge' && destinationTest) {
            list = list.filter(t => 
                t.category === destinationTest.category && 
                t.subject === destinationTest.subject
            );
        }
        return list;
    };

    const handleDestinationSelect = (testId) => {
        // Correzione eqeqeq: usa "==" solo se i tipi sono diversi (string vs int), altrimenti casta a Number
        const test = availableTests.find(t => t.id === Number(testId));
        setDestinationTest(test);
        setSelectedSources([]); 
    };

    const addToSelection = (test) => {
        if (mode === 'merge' && selectedSources.length > 0) {
            setModalState({ type: 'info', title: 'Attenzione', message: 'In modalità "Fondi" seleziona solo 1 test.' });
            return;
        }
        if (mode === 'append') {
             setSelectedSources([...selectedSources, { ...test, import_mode: 'ALL', specific_questions: [] }]);
        } else {
            setSelectedSources([...selectedSources, test]);
        }
    };

    const removeFromSelection = (testId) => setSelectedSources(selectedSources.filter(t => t.id !== testId));

    const openQuestionPicker = async (sourceTest) => {
        setLoading(true);
        try {
            const res = await axios.get(`/api_modify_test.php?action=get_questions&test_id=${sourceTest.id}`);
            if (res.data.success) {
                setQuestionPicker({
                    show: true, sourceTest: sourceTest, questions: res.data.questions,
                    selectedIds: sourceTest.specific_questions?.length > 0 ? sourceTest.specific_questions : res.data.questions.map(q => q.id),
                    filterText: ''
                });
            }
        } catch (error) { setModalState({ type: 'error', title: 'Errore', message: 'Errore recupero domande' }); } 
        finally { setLoading(false); }
    };

    const saveQuestionSelection = () => {
        const updatedSources = selectedSources.map(s => {
            if (s.id === questionPicker.sourceTest.id) {
                return {
                    ...s,
                    import_mode: questionPicker.selectedIds.length === questionPicker.questions.length ? 'ALL' : 'PARTIAL',
                    specific_questions: questionPicker.selectedIds
                };
            }
            return s;
        });
        setSelectedSources(updatedSources);
        setQuestionPicker({ ...questionPicker, show: false });
    };

    const toggleQuestionId = (id) => {
        const prev = questionPicker.selectedIds;
        setQuestionPicker({...questionPicker, selectedIds: prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]});
    };

    const initiateCommit = () => {
        if (!destinationTest || selectedSources.length === 0) {
            setModalState({ type: 'error', title: 'Dati mancanti', message: 'Seleziona destinazione e sorgenti.' });
            return;
        }

        const isMerge = mode === 'merge';
        
        // Logica Warning Tentativi
        const hasAttempts = destinationTest.attempts_count > 0;
        let warningMsg = "";
        
        if (isMerge) {
            warningMsg = (
                <div>
                    Stai per eliminare <strong>"{selectedSources[0].name}"</strong> fondendolo in <strong>"{destinationTest.name}"</strong>. 
                    <br/><br/>
                    <span className="text-danger fw-bold">ATTENZIONE:</span> I tentativi del test eliminato verranno spostati. Questa azione è irreversibile.
                </div>
            );
        } else {
            warningMsg = (
                <div>
                    Stai aggiungendo domande al test <strong>"{destinationTest.name}"</strong>.
                    {hasAttempts && (
                        <div className="alert alert-warning mt-2 small">
                            <FaExclamationTriangle className="me-1"/> Questo test ha già {destinationTest.attempts_count} tentativi. Modificarlo potrebbe alterare le statistiche.
                        </div>
                    )}
                </div>
            );
        }

        setModalState({
            type: 'confirm',
            title: isMerge ? 'Conferma Fusione (Distruttiva)' : 'Conferma Aggiunta',
            isDestructive: isMerge,
            confirmColor: isMerge ? 'btn-danger' : 'btn-primary',
            confirmAction: executeCommit,
            message: warningMsg
        });
    };

    const executeCommit = async () => {
        setLoading(true);
        try {
            const payload = {
                action: mode === 'append' ? 'append_questions' : 'merge_test',
                destination_id: destinationTest.id,
                sources: selectedSources.map(s => ({ id: s.id, mode: s.import_mode || 'ALL', questions: s.specific_questions || [] })),
                user_id: auth.user_id,
                role: auth.role,
                tenant_id: auth.tenant_id
            };
            const res = await axios.post('/api_modify_test.php', payload);
            if (res.data.success) {
                setModalState({ type: 'success', title: 'Fatto!', message: 'Operazione completata con successo.' });
            } else { throw new Error(res.data.message); }
        } catch (err) {
            setModalState({ type: 'error', title: 'Errore', message: err.message });
        } finally { setLoading(false); }
    };

    return (
        <div className="card shadow-lg border-0">
            {/* Header */}
            <div className="card-header text-white p-4" style={{ backgroundColor: theme.primary }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h4 className="mb-1 fw-bold"><FaExchangeAlt className="me-2"/> Modifica e Unisci Test</h4>
                        <p className="mb-0 fw-light text-white-50">
                            {auth.role === 'superadmin' ? 'Gestione Globale (Superadmin)' : 
                             auth.role === 'admin' ? `Gestione Tenant (Admin)` : 
                             'I miei Test e Classi (Docente)'}
                        </p>
                    </div>
                </div>
                <div className="d-flex gap-3 mt-4">
                    <button className={`btn ${mode === 'append' ? 'bg-white text-primary fw-bold' : 'btn-outline-light'} rounded-pill px-4`} onClick={() => { setMode('append'); setSelectedSources([]); }}>
                        <FaPlusCircle className="me-2"/> Aggiungi Domande
                    </button>
                    <button className={`btn ${mode === 'merge' ? 'bg-warning text-dark fw-bold' : 'btn-outline-light'} rounded-pill px-4`} onClick={() => { setMode('merge'); setSelectedSources([]); }}>
                        <FaLayerGroup className="me-2"/> Fondi Test
                    </button>
                </div>
            </div>

            <div className="card-body bg-light p-4">
                
                {/* Filtri */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="d-flex gap-3 align-items-center">
                            {auth.role === 'superadmin' ? (
                                <select className="form-select w-auto shadow-sm" value={filters.tenant_id} onChange={e => setFilters({...filters, tenant_id: e.target.value, class_id: ''})}>
                                    <option value="">-- Seleziona Azienda --</option>
                                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                </select>
                            ) : (
                                <span className="badge bg-secondary p-2"><FaLock className="me-1"/> Azienda Vincolata</span>
                            )}

                            <select className="form-select w-auto shadow-sm" value={filters.class_id} onChange={e => setFilters({...filters, class_id: e.target.value})}>
                                <option value="">Tutte le classi</option>
                                {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Destinazione */}
                <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body p-4 border-start border-5" style={{borderColor: theme.primary}}>
                        <h6 className="fw-bold text-muted mb-2">1. SELEZIONA TEST DESTINAZIONE</h6>
                        <select className="form-select form-select-lg fw-bold text-primary" value={destinationTest?.id || ''} onChange={(e) => handleDestinationSelect(e.target.value)}>
                            <option value="">-- Scegli tra i test che puoi modificare --</option>
                            {availableTests.map(t => (
                                <option key={t.id} value={t.id}>{t.name} (ID: {t.id})</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Selezione Sorgenti */}
                {destinationTest && (
                    <div className="row g-0 align-items-stretch" style={{minHeight: '400px'}}>
                        <div className="col-md-5">
                            <div className="card h-100 border shadow-sm">
                                <div className="card-header bg-white py-3 fw-bold text-muted">
                                    <FaFilter className="me-2"/> Sorgenti Disponibili
                                </div>
                                <div className="card-body p-0" style={{height: '350px', overflowY: 'auto'}}>
                                    <ul className="list-group list-group-flush">
                                        {getFilteredSourceTests().length === 0 && <li className="p-3 text-center text-muted">Nessun test compatibile.</li>}
                                        {getFilteredSourceTests().map(t => (
                                            <li key={t.id} className="list-group-item d-flex justify-content-between align-items-center py-3">
                                                <div className="text-truncate me-2">
                                                    <div className="fw-bold">{t.name}</div>
                                                    <small className="text-muted">{t.category} • {t.questions_count} dom.</small>
                                                </div>
                                                <button className="btn btn-outline-primary btn-sm rounded-circle" style={{width:35, height:35}} onClick={() => addToSelection(t)}><FaArrowRight/></button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                        
                        <div className="col-md-2 d-flex justify-content-center align-items-center text-muted">
                            <FaArrowRight size={20}/>
                        </div>

                        <div className="col-md-5">
                            <div className="card h-100 border shadow-sm" style={{borderColor: mode==='merge'?theme.accent:theme.primary}}>
                                <div className="card-header text-white py-3 fw-bold" style={{backgroundColor: mode==='merge'?theme.accent:theme.primary}}>
                                    {mode === 'append' ? 'Test da copiare' : 'Test da fondere'} ({selectedSources.length})
                                </div>
                                <div className="card-body p-0" style={{height: '350px', overflowY: 'auto', background: '#fff'}}>
                                    <ul className="list-group list-group-flush">
                                        {selectedSources.map(t => (
                                            <li key={t.id} className="list-group-item d-flex justify-content-between align-items-center py-3">
                                                <div className="w-100">
                                                    <div className="fw-bold">{t.name}</div>
                                                    {mode === 'append' && (
                                                        <div className="small mt-1">
                                                            <span className="badge border text-dark me-2">{t.import_mode==='ALL'?'Tutte':'Parziale'}</span>
                                                            <button className="btn btn-link p-0 text-decoration-none" onClick={()=>openQuestionPicker(t)}>Modifica</button>
                                                        </div>
                                                    )}
                                                </div>
                                                <button className="btn btn-outline-danger btn-sm rounded-circle" style={{width:35, height:35}} onClick={()=>removeFromSelection(t.id)}><FaTrash/></button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="text-end mt-4 pt-3 border-top">
                    <button className="btn btn-secondary me-3" onClick={() => navigate('/tests')}>Annulla</button>
                    <button className={`btn ${mode==='merge'?'btn-warning':'btn-primary'} fw-bold px-4`} onClick={initiateCommit} disabled={!destinationTest || selectedSources.length===0 || loading}>
                        {mode === 'merge' ? 'CONFERMA FUSIONE' : 'CONFERMA COPIA'}
                    </button>
                </div>
            </div>

            {/* Modali */}
            {questionPicker.show && (
                <BaseModal show={true} title="Scegli Domande" size="large" onClose={()=>setQuestionPicker({...questionPicker, show:false})} footer={<button className="btn btn-primary" onClick={saveQuestionSelection}>Salva</button>}>
                   <div className="text-start">
                       <input type="text" className="form-control mb-2" placeholder="Cerca..." value={questionPicker.filterText} onChange={e=>setQuestionPicker({...questionPicker, filterText:e.target.value})} />
                       <div className="border" style={{height:300, overflow:'auto'}}>
                           {questionPicker.questions.filter(q=>q.question_text.toLowerCase().includes(questionPicker.filterText.toLowerCase())).map(q=>(
                               <div key={q.id} className="p-2 border-bottom d-flex gap-2">
                                   <input type="checkbox" checked={questionPicker.selectedIds.includes(q.id)} onChange={()=>toggleQuestionId(q.id)} />
                                   <span>{q.question_text}</span>
                               </div>
                           ))}
                       </div>
                   </div>
                </BaseModal>
            )}
            
            <ErrorModal show={modalState.type === 'error'} onClose={closeModal} message={modalState.message} />
            <SuccessModal show={modalState.type === 'success'} onClose={closeModal} message={modalState.message} />
            <ConfirmModal show={modalState.type === 'confirm'} onClose={closeModal} onConfirm={modalState.confirmAction} title={modalState.title} message={modalState.message} isDestructive={modalState.isDestructive} confirmColor={modalState.confirmColor} />
            <InfoModal show={modalState.type === 'info'} onClose={closeModal} message={modalState.message} />
        </div>
    );
};

export default ModifyTest;